import { useCallback, useEffect, useState, useRef, useContext } from "react";
import { useSelector } from "react-redux";
import { GoogleMapsContext } from "../../context/GoogleMapsProvider";
import GoogleMapView from "../GoogleMapView";
import GoogleAddressSearch from "../GoogleAddressSearch";

export default function FormModalAppelOffre({ onClose, secteur = null, metier = null }) {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    let recognition;
    if (SpeechRecognition) {
        recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = "fr-FR";
    } else {
        console.warn("Reconnaissance vocale non supportée par ce navigateur.");
    }

    const [secteurs, setSecteurs] = useState([]);
    const [selectedSecteur, setSelectedSecteur] = useState("");
    const [metiers, setMetiers] = useState([]);
    const [selectedMetier, setSelectedMetier] = useState("");
    const [besoins, setBesoins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [secteurFocus, setSecteurFocus] = useState(false);
    const [metierFocus, setMetierFocus] = useState(false);
    const [secteurSearch, setSecteurSearch] = useState("");
    const [metierSearch, setMetierSearch] = useState("");
    const secteurInputRef = useRef();
    const metierInputRef = useRef();

    const [formData, setFormData] = useState({
        titre: "",
        description: "",
        remote: "",
        adresse: "",
        codePostal: "",
        ville: "",
        projetLat: null,
        projetLng: null,
    });

    const [selectedImages, setSelectedImages] = useState([]);
    const [selectedPDFs, setSelectedPDFs] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const { isLoaded } = useContext(GoogleMapsContext);

    const { token } = useSelector((state) => state.auth);
    const apiUrl = process.env.REACT_APP_API_URL;


    const filteredSecteurs = secteurs.filter(s =>
        secteurSearch ? s.labelSecteur.toLowerCase().includes(secteurSearch.toLowerCase()) : true
    );

    const filteredMetiers = metiers.filter(
        m =>
            m.labelMetier.toLowerCase().includes(metierSearch.toLowerCase()) &&
            !besoins.some(b => b.metierId === m.idMetier)
    );

    const addBesoin = (metier) => {
        if (besoins.some(b => b.metierId === metier.idMetier)) return;
        setBesoins(prev => [...prev, {
            metierId: metier.idMetier,
            iconMetier: metier.iconMetier,
            labelMetier: metier.labelMetier,
            nbPersonnes: 1
        }]);
    };

    const updateNbPersonnes = (metierId, nb) => {
        setBesoins(prev => prev.map(b => b.metierId === metierId ? { ...b, nbPersonnes: nb } : b));
    };

    const removeBesoin = (metierId) => {
        setBesoins(prev => prev.filter(b => b.metierId !== metierId));
    };

    const refreshSecteurs = useCallback(() => {
        setLoading(true);
        fetch(`${apiUrl}/api/secteurs`)
            .then(res => res.json())
            .then(data => setSecteurs(data))
            .catch(err => console.error("Erreur chargement secteurs :", err))
            .finally(() => setLoading(false));
    }, [apiUrl]);

    useEffect(() => refreshSecteurs(), [refreshSecteurs]);

    // --- Nouvelle initialisation Secteur + Métier depuis props ---
    useEffect(() => {
        if (secteur) {
            setSelectedSecteur(secteur.idSecteur);
            setSecteurSearch(secteur.labelSecteur);
            setMetiers(secteur.metiers || []);
        }

        if (secteur && metier) {
            setBesoins(prev => {
                const exists = prev.some(b => b.metierId === metier.idMetier);
                if (exists) return prev;
                return [
                    ...prev,
                    {
                        metierId: metier.idMetier,
                        iconMetier: metier.iconMetier,
                        labelMetier: metier.labelMetier,
                        nbPersonnes: 1
                    }
                ];
            });
            setSelectedMetier(metier.idMetier);
        }
    }, [secteur, metier]);

    useEffect(() => {
        const s = secteurs.find(sec => sec.idSecteur === parseInt(selectedSecteur));
        if (s) {
            setMetiers(s.metiers || []);
            if (!s.metiers.some(m => m.idMetier === parseInt(selectedMetier))) setSelectedMetier("");
        } else {
            setMetiers([]);
            setSelectedMetier("");
        }
    }, [selectedSecteur, secteurs]);
    const handlePlaceSelect = (place) => {
        // Extraction des composants d'adresse
        const components = place.address_components || [];
        let streetNumber = "", route = "", postal = "", city = "";

        components.forEach((c) => {
            if (c.types.includes("street_number")) streetNumber = c.long_name;
            if (c.types.includes("route")) route = c.long_name;
            if (c.types.includes("postal_code")) postal = c.long_name;
            if (c.types.includes("locality")) city = c.long_name;
            if (!city && c.types.includes("administrative_area_level_2")) city = c.long_name;
        });

        const formattedAddress = [streetNumber, route].filter(Boolean).join(" ") + ", " + postal + " " + city;

        // Fonction pour mettre à jour le formData
        const updateFormData = (lat, lng) => {
            setFormData((prev) => ({
                ...prev,
                adresse: [streetNumber, route].filter(Boolean).join(" "),
                codePostal: postal,
                ville: city,
                projetLat: lat,
                projetLng: lng,
            }));

        };

        // Vérifie si place.geometry existe
        if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            updateFormData(lat, lng);
        } else {
            // Sinon, utiliser Geocoder pour obtenir lat/lng à partir de l'adresse
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: formattedAddress }, (results, status) => {
                if (status === "OK" && results[0]) {
                    const lat = results[0].geometry.location.lat();
                    const lng = results[0].geometry.location.lng();
                    updateFormData(lat, lng);
                } else {
                    console.error("Impossible de récupérer les coordonnées :", status, place);
                    updateFormData(null, null); // vide lat/lng si échec
                }
            });
        }
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleDropFiles = (files, type) => {
        if (type === "images") {
            const newImgs = Array.from(files).map(f => ({ file: f, url: URL.createObjectURL(f) }));
            setSelectedImages(prev => [...prev, ...newImgs]);
        } else if (type === "pdfs") {
            setSelectedPDFs(prev => [...prev, ...Array.from(files)]);
        }
    };

    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (file) setSelectedVideo({ file, url: URL.createObjectURL(file) });
    };

    const handleRemoveFile = (idx, type) => {
        if (type === "images") setSelectedImages(prev => prev.filter((_, i) => i !== idx));
        if (type === "pdfs") setSelectedPDFs(prev => prev.filter((_, i) => i !== idx));
        if (type === "video") setSelectedVideo(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            onClose();
            const pendingData = { ...formData, secteur: selectedSecteur, besoins, images: selectedImages, pdfs: selectedPDFs, video: selectedVideo };
            window.dispatchEvent(new CustomEvent("openRegisterModal", { detail: { form: pendingData } }));
            return;
        }

        try {
            const form = new FormData();
            Object.entries(formData).forEach(([key, value]) => form.append(key, value));
            form.append("secteur", selectedSecteur);
            besoins.forEach((b, i) => {
                form.append(`besoins[${i}][metierId]`, b.metierId);
                form.append(`besoins[${i}][nbPersonnes]`, b.nbPersonnes);
            });
            selectedImages.forEach((img) => form.append("images[]", img.file));
            selectedPDFs.forEach((pdf) => form.append("pdfs[]", pdf));
            if (selectedVideo) form.append("video", selectedVideo.file);

            const response = await fetch(`${apiUrl}/api/appelsOffres`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.message || "Erreur lors de l’ajout de l'appel d'offre");
                return;
            }

            const data = await response.json();
            console.log("Projet ajouté :", data);
            onClose();
        } catch (err) {
            console.error("Erreur envoi projet :", err);
            alert("Impossible de contacter le serveur.");
        }
    };

    const preventDefaults = (e) => { e.preventDefault(); e.stopPropagation(); };
    const dropZoneImages = useRef();
    const dropZonePDFs = useRef();
    const isFormValid =
        selectedSecteur &&
        besoins.length > 0 &&
        formData.titre.trim().length > 0 &&
        formData.description.trim().length > 0 &&
        formData.remote &&
        formData.projetLat !== null &&
        formData.projetLng !== null;
    return (
        <div className="modalOverlay">
            <div className="modalContainer">
                <button className="modalClose" onClick={onClose}>×</button>
                <h2>Déposer un appel d'offres</h2>
                <form onSubmit={handleSubmit}>

                    {/* Secteur */}
                    <div className="modal-input-form">
                        <label>Secteur d'activité</label>
                        <div className="input-with-icon">
                            {recognition && (
                                <button type="button" className="voice-btn" onClick={() => {
                                    recognition.start();
                                    recognition.onresult = (event) => {
                                        const transcript = event.results[0][0].transcript;
                                        setSecteurSearch(transcript);
                                        secteurInputRef.current?.focus();
                                    };
                                }}>🎤</button>
                            )}
                            <input
                                ref={secteurInputRef}
                                type="text"
                                value={secteurSearch}
                                onChange={(e) => setSecteurSearch(e.target.value)}
                                placeholder="Tapez ou dictez pour filtrer les secteurs..."
                                onFocus={() => setSecteurFocus(true)}
                                onBlur={() => setTimeout(() => setSecteurFocus(false), 150)}
                            />
                        </div>

                        {secteurFocus && filteredSecteurs.length > 0 && (
                            <ul className="secteur-dropdown">
                                {filteredSecteurs.map(s => (
                                    <li key={s.idSecteur} className="secteur-dropdown-item" onClick={() => {
                                        setSelectedSecteur(s.idSecteur);
                                        setSecteurSearch(s.labelSecteur);
                                        setSecteurFocus(false);
                                    }}>{s.labelSecteur}</li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Métiers */}
                    {metiers.length > 0 && (
                        <div className="modal-input-form">
                            <label>Ajouter un métier</label>
                            <div className="metier-search-container">
                                <div className="input-with-icon">
                                    {recognition && (
                                        <button type="button" className="voice-btn" onClick={() => {
                                            recognition.start();
                                            recognition.onresult = (event) => {
                                                const transcript = event.results[0][0].transcript;
                                                setMetierSearch(transcript);
                                                metierInputRef.current?.focus();
                                                setMetierFocus(true);
                                            };
                                        }}>🎤</button>
                                    )}
                                    <input
                                        ref={metierInputRef}
                                        type="text"
                                        value={metierSearch}
                                        onChange={(e) => setMetierSearch(e.target.value)}
                                        placeholder="Tapez ou dictez pour filtrer les métiers..."
                                        className="metier-search-input"
                                        onFocus={() => setMetierFocus(true)}
                                        onBlur={() => setTimeout(() => setMetierFocus(false), 150)}
                                    />
                                </div>

                                {metierFocus && filteredMetiers.length > 0 && (
                                    <ul className="metier-dropdown">
                                        {filteredMetiers.map(m => (
                                            <li key={m.idMetier} className="metier-dropdown-item" onClick={() => {
                                                addBesoin(m);
                                                setMetierSearch("");
                                                setMetierFocus(false);
                                            }}>
                                                <img src={`${apiUrl}/api/files/?categorie=metiers&file=${m.iconMetier}`} alt={m.labelMetier} className="metier-icon" />
                                                <span>{m.labelMetier}</span>
                                                <button type="button" className="add-besoin-btn">Ajouter</button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {metierFocus && filteredMetiers.length === 0 && metierSearch && <p className="no-results">Aucun métier trouvé</p>}
                            </div>
                        </div>
                    )}

                    {/* Besoins ajoutés */}
                    {besoins.length > 0 && (
                        <div className="modal-input-form">
                            <label>Métiers sélectionnés</label>
                            <div className="besoin-grid">
                                {besoins.map(b => {
                                    const iconUrl = `${apiUrl}/api/files/?categorie=metiers&file=${b.iconMetier}`;
                                    return (
                                        <div key={b.metierId} className="besoin-card">
                                            <div className="besoin-header">
                                                <div className="besoin-icon-label">
                                                    <img src={iconUrl} alt={b.labelMetier} className="besoin-icon" />
                                                    <span>{b.labelMetier}</span>
                                                </div>
                                                <button type="button" className="remove-besoin" onClick={() => removeBesoin(b.metierId)}>×</button>
                                            </div>
                                            <div className="besoin-controls">
                                                <button type="button" onClick={() => updateNbPersonnes(b.metierId, Math.max(1, b.nbPersonnes - 1))}>–</button>
                                                <input type="number" min="1" value={b.nbPersonnes} onChange={(e) => updateNbPersonnes(b.metierId, parseInt(e.target.value))} />
                                                <button type="button" onClick={() => updateNbPersonnes(b.metierId, b.nbPersonnes + 1)}>+</button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Titre */}
                    <div className="modal-input-form">
                        <label>Titre du projet</label>
                        <div className="input-with-icon">
                            {recognition && (
                                <button
                                    type="button"
                                    className="voice-btn"
                                    onClick={() => {
                                        recognition.start();
                                        recognition.onresult = (event) => {
                                            const transcript = event.results[0][0].transcript;
                                            setFormData(prev => ({ ...prev, titre: transcript }));
                                        };
                                    }}
                                >🎤</button>
                            )}
                            <input
                                type="text"
                                name="titre"
                                value={formData.titre}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    {/* Description */}
                    <div className="modal-input-form">
                        <label>Description du projet</label>
                        <div className="textarea-with-icon">
                            {recognition && (
                                <button
                                    type="button"
                                    className="voice-btn-textarea"
                                    onClick={() => {
                                        recognition.start();
                                        recognition.onresult = (event) => {
                                            const transcript = event.results[0][0].transcript;
                                            setFormData(prev => ({ ...prev, description: transcript }));
                                        };
                                    }}
                                >🎤</button>
                            )}
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Décrivez votre projet en détail..."
                                required
                            />
                        </div>
                    </div>

                    <div className="modal-input-form">
                        <label>Votre projet est-il réalisable à distance ?</label>
                        <select name="remote" value={formData.remote} onChange={handleChange} required>
                            <option value="">-- Sélectionnez --</option>
                            <option value="oui">Oui</option>
                            <option value="non">Non</option>
                        </select>
                    </div>
                                        <div className="modal-input-form">
                        <label>Rechercher et sélectionner l'adresse de votre appel d'offre, pour etre valide, la carte doit s'afficher après selection</label>
                        {isLoaded && (
                            <GoogleAddressSearch
                                value={[formData.adresse, formData.codePostal, formData.ville].filter(Boolean).join(", ")}
                                onChange={(inputValue) => {
                                    // Si l'utilisateur vide le champ, on supprime les infos d'adresse
                                    if (!inputValue) {
                                        setFormData(prev => ({
                                            ...prev,
                                            adresse: "",
                                            codePostal: "",
                                            ville: "",
                                            projetLat: null,
                                            projetLng: null
                                        }));
                                    }
                                }}
                                onSelect={handlePlaceSelect}
                            />
                        )}
                        {formData.projetLat && formData.projetLng && (
                            <GoogleMapView origin={{ lat: formData.projetLat, lng: formData.projetLng }} />
                        )}
                    </div>
                    {/* Uploads */}
                    <div className="modal-input-form" ref={dropZoneImages} onDragEnter={preventDefaults} onDragOver={preventDefaults} onDrop={(e) => { preventDefaults(e); handleDropFiles(e.dataTransfer.files, "images"); }}>
                        <label className="btn-label-add-files">Ajouter des photos
                            <input type="file" accept="image/*" multiple onChange={(e) => handleDropFiles(e.target.files, "images")} style={{ display: "none" }} />
                        </label>
                        <div className="image-previews">
                            {selectedImages.map((img, idx) => (
                                <div key={idx} className="file-preview">
                                    <img src={img.url} alt="preview" />
                                    <button type="button" onClick={() => handleRemoveFile(idx, "images")}>×</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="modal-input-form" ref={dropZonePDFs} onDragEnter={preventDefaults} onDragOver={preventDefaults} onDrop={(e) => { preventDefaults(e); handleDropFiles(e.dataTransfer.files, "pdfs"); }}>
                        <label className="btn-label-add-files">Ajouter des documents PDF
                            <input type="file" accept="application/pdf" multiple onChange={(e) => handleDropFiles(e.target.files, "pdfs")} style={{ display: "none" }} />
                        </label>
                        <ul className="file-previews">
                            {selectedPDFs.map((pdf, idx) => (
                                <li key={idx}>{pdf.name} <button type="button" onClick={() => handleRemoveFile(idx, "pdfs")}>×</button></li>
                            ))}
                        </ul>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={onClose}>Annuler</button>
                        <button type="submit" disabled={!isFormValid}>Ajouter</button>
                    </div>

                </form>
            </div>
        </div>
    );
}
